import { Injectable, inject } from '@angular/core';
import {
  Addresses,
  Agreements,
  Appellations,
  BuySpecs,
  Companies,
  Contacts,
  DataType,
  DataTypeKeys,
  File,
  Invoices,
  Items,
  Logs,
  Reminders,
  SampleShipmentRequests,
  SampleShipments,
  Samples,
  ShipmentFulfillments,
  StoreDataTypeKey,
  Varietals,
} from '@etoh/database/core';
import { ColDef, RowClassParams, RowClassRules } from 'ag-grid-community';
import { capitalize, get, isArray, isNumber, sortBy, sum, uniq } from 'lodash';
import { firstValueFrom, take } from 'rxjs';
import { formatDate, formatPercent } from '../../locales/locales';
import { StoreService } from '../store.service';
import {
  currencyFormatter,
  dateFormatter,
  getDisplaysForVariatealOrVintages,
  getValueForOptions,
  numberFormatter,
  percentFormatter,
  plainTextFromFormatter,
} from './components/table/formatter';
import { getCountryOptions } from './forms/countries.datas';
import {
  agreementsContractStatusOptions,
  agreementsFulfilledStatusOptions,
  sampleShipmentStatusOptions,
  statusOptionsShipmentFulfillment,
} from './forms/options';

import { Contracts, Users } from '@etoh/database/angular';
import {
  commodityOptions,
  currencyOptions,
  incotermsOptions,
} from '@etoh/database/core';
import { isSameYear, parseISO } from 'date-fns';
import { NzMessageService } from 'ng-zorro-antd/message';
import { convertCurrencyToUSD } from '../../currenciesConversions/pure';
import { hasUserRole } from '../../roles';
import { UserService } from '../../user.service';
import { getFulfillmentsStatus } from '../agreements/agreements.pure';
import { AgreementsService } from '../agreements/agreements.service';
import {
  isAgreementFinished,
  isAgreementInProgress,
  isAgreementLost,
} from '../buy-specs/buy-specs.pure';
import { ContactsService } from '../contacts/contacts.service';
import { getSamplesShipmentRequestsToSend } from '../sample-shipment-requests/sample-shipment-requests.pure';
import { SampleShipmentsService } from '../sample-shipments/sample-shipments.service';
import { getSamplesAvailableByItemId } from '../samples/sample.pure';
import { UsersService } from '../users.service';
import { LinkComponent } from './components/table/cells/link/link.component';
import { getUom } from './forms/uom';
import { ColDefExtended, LegendAgGrid } from './list.interfaces';

interface LegendAgGridConditions extends LegendAgGrid {
  conditions: ((params: RowClassParams<any>) => boolean) | string;
}

interface ColumnsDefs {
  companies: () => Promise<ColDefExtended<Companies>[]>;
  contacts: () => Promise<ColDefExtended<Contacts>[]>;
  addresses: () => Promise<ColDefExtended<Addresses>[]>;
  items: () => Promise<ColDefExtended<Items>[]>;
  buySpecs: () => Promise<ColDefExtended<BuySpecs>[]>;
  samples: () => Promise<ColDefExtended<Samples>[]>;
  invoices: () => Promise<ColDefExtended<Invoices>[]>;
  agreements: () => Promise<ColDefExtended<Agreements>[]>;
  shipmentFulfillments: () => Promise<ColDefExtended<ShipmentFulfillments>[]>;
  sampleShipmentRequests: () => Promise<
    ColDefExtended<SampleShipmentRequests>[]
  >;
  sampleShipments: () => Promise<
    ColDefExtended<Partial<SampleShipments & any>>[]
  >;
  reminders: () => Promise<ColDefExtended<Reminders>[]>;
  contracts: () => Promise<ColDefExtended<Partial<Contracts>>[]>;
  logs: () => Promise<ColDefExtended<Logs>[]>;
  varietals: () => Promise<ColDefExtended<Varietals>[]>;
  appellations: () => Promise<ColDefExtended<Appellations>[]>;
  users: () => Promise<ColDefExtended<Users>[]>;
}

type KeycolumnsDefs = keyof ColumnsDefs;

const AppellationTreeColumns = [
  {
    field: 'appellations',
    valueGetter: (params: any) => {
      return (params.data as Items)?.appellations.map((v) => v.type);
    },
    valueFormatter: (params: any) => {
      return getDisplaysForVariatealOrVintages(params?.data?.appellations);
    },
  },
  {
    field: 'country',
    roles: ['user'],
  },
  {
    field: 'state',
    roles: ['user'],
  },
  {
    field: 'region',
    roles: ['user'],
  },
  {
    field: 'subRegion',
    roles: ['user'],
  },
  {
    field: 'subAppellation',
    roles: ['user'],
  },
  {
    field: 'county',
    roles: ['user'],
  },
  {
    field: 'district',
    roles: ['user'],
  },
];

const VarietalsColumnd = [
  // TODO csv export use cellback process
  {
    field: 'varietals',
    valueGetter: (params: any) => {
      return (params.data as Items).varietals.map((v) => v.type);
    },
    valueFormatter: (params: any) => {
      return getDisplaysForVariatealOrVintages(params.data?.varietals);
    },
  },
];

const VintagesColumnd = [
  {
    field: 'vintages',
    valueGetter: (params: any) => {
      return (params.data as Items).vintages.map((v) => v.type);
    },
    valueFormatter: (params: any) => {
      return getDisplaysForVariatealOrVintages(params.data?.vintages);
    },
  },
];

@Injectable({
  providedIn: 'root',
})
export class ListService {
  public columnDefs: ColumnsDefs;

  private userService = inject(UserService);
  private usersService = inject(UsersService);
  private contactsService = inject(ContactsService);
  private storeService = inject(StoreService);
  private agreementsService = inject(AgreementsService);
  private sampleShipmentsService = inject(SampleShipmentsService);
  private messages = inject(NzMessageService);

  public formatter: Partial<
    Record<DataTypeKeys, (data: DataType[DataTypeKeys]) => string>
  > = {
    users: (user) => {
      return this.usersService.getLabelForUser(user as Users);
    },
    contacts: (contact: any) => {
      return this.contactsService.getLabelForContact(contact);
    },
    addresses: (address: any) => {
      return address?.name;
    },
    offices: (office: any) => {
      return office?.name;
    },
    companies: (company: any) => {
      return company?.name;
    },
    items: (company: any) => {
      return company?.id;
    },
  };

  private getLegendsAgGridConditions = (
    dataType: DataTypeKeys
  ): LegendAgGridConditions[] => {
    if (dataType === 'sampleShipmentRequests') {
      return [
        {
          label: 'No samples in stock',
          class: 'warning',
          conditions: (params) => {
            console.log('params', params);
            const itemId = params.data?.itemId;

            if (!itemId) {
              return false;
            }

            const samplesOfItems = getSamplesAvailableByItemId(
              params.data.samplesByItemId,
              itemId
            );

            return samplesOfItems.length === 0;
          },
        },
      ];
    } else if (dataType === 'samples') {
      return [
        {
          label: 'Item is archived',
          class: 'warning',
          conditions: (params) => {
            return !!params.data?.item?.archivedAt;
          },
        },
      ];
    } else if (dataType === 'buySpecs') {
      return [
        {
          label: 'Agreement settled',
          class: 'success',
          conditions: (params) => {
            const buySpecs: BuySpecs = params.data;
            return isAgreementFinished(buySpecs);
          },
        },
        {
          label: 'Agreement in progress',
          class: 'warning',
          conditions: (params) => {
            const buySpecs: BuySpecs = params.data;
            return isAgreementInProgress(buySpecs);
          },
        },
        {
          label: 'Lost Buy Spec',
          class: 'danger',
          conditions: (params) => {
            const buySpecs: BuySpecs = params.data;
            return isAgreementLost(buySpecs);
          },
        },
      ];
    } else if (dataType === 'contracts') {
      return [
        {
          label: 'Contract signed',
          class: 'success',
          conditions: (params) => {
            return params.data.status === 'validated';
          },
        },
        /*
        {
          label: 'Contract waiting',
          class: 'danger',
          conditions: (params) => {
            return params.data.status === 'waiting';
          }
        },
        */
        {
          label: 'Contract refused',
          class: 'danger',
          conditions: (params) => {
            return params.data.status === 'refused';
          },
        },
      ];
    }

    return [];
  };

  public getRowLegends = (dataType: DataTypeKeys): LegendAgGrid[] => {
    const legendConditions: LegendAgGridConditions[] =
      this.getLegendsAgGridConditions(dataType);

    return legendConditions.map((v) => {
      return {
        label: v.label,
        class: v.class,
      };
    });
  };

  public getRowClassRules = (dataType: DataTypeKeys): RowClassRules<any> => {
    console.log('getRowClassRules', dataType);

    const legendConditions: LegendAgGridConditions[] =
      this.getLegendsAgGridConditions(dataType);

    return legendConditions.reduce((acc, curr) => {
      (acc as any)[curr.class] = curr.conditions;
      return acc;
    }, {}) as RowClassRules<any>;
  };

  constructor() {
    this.initColumnDefs();
  }

  private initColumnDefs() {
    this.columnDefs = {
      logs: async () => {
        return [
          {
            field: 'action',
          },
          {
            field: 'url',
          },
          {
            field: 'payload.cellsLength',
            filter: 'agNumberColumnFilter',
            valueFormatter: numberFormatter,
          } as any,
          {
            field: 'payload.dataType',
          },
          {
            field: 'payload.type',
          },
        ];
      },
      users: async () => {
        return [
          {
            field: 'firstname',
          },
          {
            field: 'lastname',
          },
          {
            field: 'isManager',
          },
          {
            field: 'isAdmin',
          },
          {
            field: 'officeId',
            dataType: 'offices',
          },
          {
            field: 'role',
          },
        ];
      },
      invoices: async () => {
        const currenciesConversions = await firstValueFrom(
          this.storeService.store.currenciesConversions.data$.pipe(take(1))
        );

        return [
          {
            field: 'agreementId',
            filter: 'agNumberColumnFilter',
          },
          {
            headerName: 'Invoice Month',
            valueGetter: (params: any) => {
              const date: string | null = params.data?.createdAt;
              if (!date) {
                return null;
              }

              return capitalize(
                formatDate(params.data.createdAt, 'MMMM') ?? ''
              );
            },
          },
          {
            headerName: 'Invoice Year',
            filter: 'agNumberColumnFilter',
            valueGetter: (params: any) => {
              const date: string | null = params.data?.createdAt;
              if (!date) {
                return null;
              }

              return parseISO(date).getFullYear();
            },
          },
          {
            field: 'buyerPoRef',
          },
          {
            field: 'agreement.sellerCompanyId',
            dataType: 'companies',
          },
          {
            field: 'supplierInvoice',
          },
          {
            field: 'agreement.buyerCompanyId',
            dataType: 'companies',
          },
          {
            field: 'shipmentFulfillment.itemId',
            dataType: 'items',
          },
          {
            headerName: 'Item Estimated Quantity',
            filter: 'agNumberColumnFilter',
            valueGetter: (params: any) => {
              const agreeement: Agreements = params.data?.agreement;

              const itemInCostArray = agreeement?.costArray?.find((cost) => {
                return cost.itemId === params.data?.shipmentFulfillment?.itemId;
              });

              return itemInCostArray?.quantity;
            },
            valueFormatter: (params: any) => {
              return numberFormatter(
                params,
                getUom(params?.data?.shipmentFulfillment?.uom)
              );
            },
          },
          {
            headerName: 'Item Quantity Invoiced',
            filter: 'agNumberColumnFilter',
            valueGetter: (params: any) => {
              return params.data?.shipmentFulfillment?.quantity;
            },
            valueFormatter: (params: any) => {
              return numberFormatter(
                params,
                getUom(params?.data?.shipmentFulfillment?.uom)
              );
            },
          },
          {
            field: 'shipmentFulfillment.itemId',
          },
          {
            field: 'shipmentFulfillment.uom',
          },
          {
            headerName: 'Item Unit Price',
            filter: 'agNumberColumnFilter',
            valueGetter: (params: any) => {
              const agreeement: Agreements = params.data?.agreement;

              const itemInCostArray = agreeement?.costArray?.find((cost) => {
                return cost.itemId === params.data?.shipmentFulfillment?.itemId;
              });

              return itemInCostArray?.unitPrice;
            },
            valueFormatter: (params: any) => {
              return currencyFormatter(params, params.data?.currency);
            },
          },
          {
            headerName: 'Item Unit Price ($USD)',
            filter: 'agNumberColumnFilter',
            valueGetter: (params: any) => {
              const agreeement: Agreements = params.data?.agreement;

              const itemInCostArray = agreeement?.costArray?.find((cost) => {
                return cost.itemId === params.data?.shipmentFulfillment?.itemId;
              });

              if (!itemInCostArray?.currency) {
                return;
              }

              return convertCurrencyToUSD(
                currenciesConversions,
                agreeement.createdAt,
                itemInCostArray?.currency,
                itemInCostArray?.unitPrice
              );
            },
            valueFormatter: (params: any) => {
              return currencyFormatter(params, 'USD', '1.0-0');
            },
          },
          {
            field: 'agreement.totalCost',
            filter: 'agNumberColumnFilter',
            valueFormatter: (params: any) => {
              return currencyFormatter(params, params.data?.currency);
            },
          },

          {
            headerName: 'Agreement Commission',
            filter: 'agNumberColumnFilter',
            valueGetter: (params: any) => {
              const agreeement: Agreements = params.data?.agreement;

              return this.agreementsService.getCommissionFromAgreement(
                agreeement
              );
            },
            valueFormatter: (params: any) => {
              return currencyFormatter(params, params.data?.currency);
            },
          },

          {
            headerName: 'Agreement Commission ($USD)',
            filter: 'agNumberColumnFilter',
            valueGetter: (params: any) => {
              const agreeement: Agreements = params.data?.agreement;

              const commission =
                this.agreementsService.getCommissionFromAgreement(agreeement);

              if (!isNumber(commission)) {
                return;
              }

              return convertCurrencyToUSD(
                currenciesConversions,
                agreeement.createdAt,
                params.data?.currency,
                commission
              );
            },
            valueFormatter: (params: any) => {
              return currencyFormatter(params, 'USD', '1.0-0');
            },
          },

          {
            field: 'amount',
            filter: 'agNumberColumnFilter',
            valueFormatter: (params: any) => {
              return currencyFormatter(params, params.data?.currency);
            },
          },
          {
            headerName: 'Amount ($USD)',
            filter: 'agNumberColumnFilter',
            valueGetter: (params: any) => {
              return convertCurrencyToUSD(
                currenciesConversions,
                params.data?.createdAt,
                params.data?.currency,
                params.data?.amount
              );
            },
            valueFormatter: (params: any) => {
              return currencyFormatter(params, 'USD', '1.0-0');
            },
          },
          {
            field: 'currency',
            valueGetter: (params: any) =>
              getValueForOptions(
                'currency',
                currencyOptions,
                params.data?.currency
              ),
          },
          {
            field: 'companyId',
            dataType: 'companies',
          },
          {
            field: 'officeId',
            dataType: 'offices',
          },
          {
            field: 'agreement.commodityType',
            valueGetter: (params: any) =>
              getValueForOptions(
                'commodityType',
                commodityOptions,
                params.data?.agreement?.commodityType
              ),
          },
          {
            field: 'agreement.buyerContractRef',
          },
          {
            field: 'agreement.sellerContractRef',
          },

          {
            field: 'brokerId',
            dataType: 'users',
          },
          {
            field: 'shipmentFulfillmentId',
            headerName: 'Item Fulfillment',
          },
          {
            field: 'status',
          },

          {
            headerName: 'Payment Date (Month)',
            valueGetter: (params: any) => {
              const date: string | null = params.data?.paymentDate;
              if (!date) {
                return null;
              }

              return parseISO(date).getMonth();
            },
            valueFormatter: (params: any) => {
              return formatDate(params.value, 'MMMM');
            },
          },
          {
            headerName: 'Payment Date (Year)',
            filter: 'agNumberColumnFilter',
            valueGetter: (params: any) => {
              const date: string | null = params.data?.paymentDate;
              if (!date) {
                return null;
              }

              return parseISO(date).getFullYear();
            },
          },
          {
            field: 'paymentDate',
            valueFormatter: dateFormatter,
            filter: 'agDateColumnFilter',
          },
          {
            field: 'quickbookId',
            filter: 'agNumberColumnFilter',
            valueFormatter: numberFormatter,
          },
          {
            field: 'comments',
            valueFormatter: plainTextFromFormatter as any,
            exportType: 'htmlToText',
          },
          {
            field: 'agreement.buyerOfficeId',
            dataType: 'offices',
          },
          {
            headerName: 'Agreement Buyer Broker',
            field: 'agreement.buyerUserId',
            dataType: 'users',
          },
        ] as any;
      },

      contracts: async () => {
        return [
          {
            field: 'agreementId',
            dataType: 'agreements',
          },
          {
            field: 'companiesInvolved',
            dataType: 'companies',
          },
          {
            field: 'status',
          },
          {
            field: 'version',
          },
        ] as any[];
      },

      reminders: async () => {
        return [
          {
            field: 'title',
          },
          {
            field: 'assignedTo',
            dataType: 'users',
          },
          {
            field: 'description',
          },
          {
            field: 'startDate',
            valueFormatter: dateFormatter,
            filter: 'agDateColumnFilter',
          },
          {
            field: 'viewedAt',
            valueFormatter: dateFormatter,
            filter: 'agDateColumnFilter',
          },
        ];
      },

      sampleShipmentRequests: async () => {
        const buySpecsByKeys = await firstValueFrom(
          this.storeService.getStoreByKeys$<BuySpecs>('buySpecs').pipe(take(1))
        );

        const sampleShipmentRequests = await firstValueFrom(
          this.storeService.store.sampleShipmentRequests.data$.pipe(take(1))
        );

        const addresses = await firstValueFrom(
          this.storeService
            .getStoreByKeys$<Addresses>('addresses')
            .pipe(take(1))
        );

        return [
          {
            field: 'buySpecId',
            filter: 'agNumberColumnFilter',
          },
          {
            field: 'itemId',
            dataType: 'items',
            filter: 'agNumberColumnFilter',
          },
          {
            field: 'numberOfSamples',
            filter: 'agNumberColumnFilter',
            valueFormatter: numberFormatter,
          },
          {
            field: 'type',
          },
          {
            field: 'requestedBy',
            dataType: 'users',
          },
          {
            headerName: 'Samples in stock',
            colId: 'samplesInStock',
            filter: 'agNumberColumnFilter',
            valueFormatter: numberFormatter,
            valueGetter: (params: any) => {
              const itemId = params.data?.itemId;

              if (!itemId) {
                return null;
              }

              const samplesOfItems = getSamplesAvailableByItemId(
                params.data.samplesByItemId,
                itemId
              );

              return samplesOfItems.length;
            },
          },
          {
            headerName: 'Samples to replenish',
            filter: 'agNumberColumnFilter',
            valueFormatter: numberFormatter,
            colId: 'samplesToReplenish',
            valueGetter: (params: any) => {
              const itemId = (params.data as any)?.itemId;

              if (!itemId && !params.data?.sampleShipmentId) {
                return null;
              }

              const samplesOfItemsAvailables = getSamplesAvailableByItemId(
                params.data.samplesByItemId,
                itemId
              );

              const samplesToSend = sum(
                getSamplesShipmentRequestsToSend(
                  sampleShipmentRequests,
                  itemId
                ).map((s) => s.numberOfSamples)
              );

              const availableSamplesAfterSending =
                samplesOfItemsAvailables.length - samplesToSend;

              if (availableSamplesAfterSending < 0) {
                return -availableSamplesAfterSending;
              } else {
                return null;
              }

              return null;
            },
          },
          {
            headerName: 'Address',
            rowGroup: true,
            hide: false,
            valueGetter: (params: any) => {
              if (!params.data) {
                return null;
              }

              const buySpecId = params.data?.buySpecId;

              if (buySpecId) {
                const buySpec = buySpecsByKeys[buySpecId];
                return addresses[(buySpec as any)?.buyerAddressId]?.name;
              }

              return 'Undefined';
            },
          },
          {
            field: 'deadline',
            valueFormatter: dateFormatter,
            filter: 'agDateColumnFilter',
          },
          {
            field: 'sampleShipmentId',
            filter: 'agNumberColumnFilter',
          },
          {
            field: 'buySpec.buyerCompanyId',
            dataType: 'companies',
          },
          {
            field: 'item.officeId',
            dataType: 'offices',
          },
          {
            field: 'item.supplierCompanyId',
            dataType: 'companies',
          },
          {
            field: 'item.commodityType',
            valueGetter: (params: any) =>
              getValueForOptions(
                'commodityType',
                commodityOptions,
                params.data?.item?.commodityType
              ),
          },
          {
            field: 'varietals',
            valueGetter: (params: any) => {
              return (params.data?.item as Items)?.varietals?.map(
                (v) => v.type
              );
            },
            valueFormatter: (params: any) => {
              return getDisplaysForVariatealOrVintages(
                params.data?.item?.varietals
              );
            },
          },
          {
            field: 'vintages',
            valueGetter: (params: any) => {
              return (params.data?.item as Items)?.vintages?.map((v) => v.type);
            },
            valueFormatter: (params: any) => {
              return getDisplaysForVariatealOrVintages(
                params.data?.item?.vintages
              );
            },
          },

          {
            field: 'item.generic',
          },
          {
            field: 'item.country',
          },
          {
            field: 'item.state',
          },
          {
            field: 'item.appellations',
            valueGetter: (params: any) => {
              return (params.data?.item as Items)?.appellations?.map(
                (v) => v.type
              );
            },
            valueFormatter: (params: any) => {
              return getDisplaysForVariatealOrVintages(
                params?.data?.item?.appellations
              );
            },
          },
          {
            field: 'comments',
            valueFormatter: plainTextFromFormatter as any,
            exportType: 'htmlToText',
          },
        ] as any;
      },

      sampleShipments: async () => {
        const carriersOptions = await firstValueFrom(
          this.sampleShipmentsService.allCarriersOptions.pipe(take(1))
        );

        return [
          {
            field: 'pickupDate',
            valueFormatter: dateFormatter,
            filter: 'agDateColumnFilter',
          },

          {
            field: 'deliveryDate',
            valueFormatter: dateFormatter,
            filter: 'agDateColumnFilter',
          },

          {
            field: 'status',
            valueGetter: (params: any) =>
              getValueForOptions(
                'sampleShipmentStatus',
                sampleShipmentStatusOptions,
                params.data?.status
              ),
          },

          {
            field: 'brokerId',
            dataType: 'users',
          },

          {
            field: 'recipientCompanyId',
            dataType: 'companies',
          },

          {
            field: 'recipientContactId',
            dataType: 'contacts',
          },
          {
            field: 'recipientAddressId',
            dataType: 'addresses',
          },

          {
            field: 'officeId',
            dataType: 'offices',
          },
          {
            field: 'trackingId',
          },
          {
            field: 'carrier',
            valueGetter: (params: any) => {
              return getValueForOptions(
                'carrierOptions',
                carriersOptions,
                params.data?.carrier
              );
            },
          },
          {
            field: 'details',
          },
          {
            field: 'senderCompanyId',
            dataType: 'companies',
          },
          {
            field: 'senderContactId',
            dataType: 'contacts',
          },
          {
            field: 'senderAddressId',
            dataType: 'addresses',
          },
          {
            field: 'sampleShipmentRequests.type',
            valueGetter: (params: any) => {
              const sampleShipment: SampleShipments = params.data;
              const sampleShipmentRequestsType =
                sampleShipment?.sampleShipmentRequests?.map((ssr) => ssr.type);
              return uniq(sampleShipmentRequestsType);
            },
          },
        ];
      },

      items: async () => {
        const currenciesConversions = await firstValueFrom(
          this.storeService.store.currenciesConversions.data$.pipe(take(1))
        );

        return [
          {
            field: 'supplierCompanyId',
            dataType: 'companies',
            roles: ['user'],
          },
          {
            field: 'tankNo',
          },
          {
            field: 'lotNo',
          },
          {
            field: 'quantity',

            ...(this.userService.role$.value === 'user'
              ? this.editField<Items>('items', (val) => {
                  return { quantity: <number>val };
                })
              : {}),

            filter: 'agNumberColumnFilter',
            valueFormatter: (params: any) => {
              return numberFormatter(params, getUom(params?.data?.uom));
            },
          },
          ...VarietalsColumnd,
          ...VintagesColumnd,
          {
            field: 'generic',
          },
          ...AppellationTreeColumns,
          {
            field: 'localPrice',
            filter: 'agNumberColumnFilter',
            ...(this.userService.role$.value === 'user'
              ? this.editField<Items>('items', (val) => {
                  return { localPrice: <number>val };
                })
              : {}),
            valueFormatter: (params: any) => {
              return currencyFormatter(params, params?.data?.currency);
            },
          },
          {
            headerName: 'Local Price ($USD)',
            filter: 'agNumberColumnFilter',
            valueGetter: (params: any) => {
              return convertCurrencyToUSD(
                currenciesConversions,
                params.data?.createdAt,
                params.data?.currency,
                params.data?.localPrice
              );
            },
            valueFormatter: (params: any) => {
              return currencyFormatter(params, 'USD', '1.0-0');
            },
          },

          {
            field: 'currency',
            valueGetter: (params: any) =>
              getValueForOptions(
                'currency',
                currencyOptions,
                params.data?.currency
              ),
          },

          {
            field: 'exportIncotermsType',
          },
          {
            field: 'exportIncoterms',
          },

          {
            field: 'isOpportunistic',
            roles: ['user'],
          },

          {
            field: 'uom',
            roles: ['user'],
            valueFormatter: (params: any) => {
              return getUom(params?.data?.uom);
            },
          },

          {
            field: 'brokerId',
            dataType: 'users',
            roles: ['user'],
          },

          {
            field: 'certifications',
          },

          {
            field: 'qualities',
            roles: ['user'],
          },

          {
            field: 'samples',
            filter: 'agNumberColumnFilter',
            valueFormatter: numberFormatter,
            valueGetter: (params: any) => {
              const samplesNonArchived = (params.data?.samples || [])?.filter(
                (s: Samples) => !(s.archivedAt || s.deletedAt)
              );
              return samplesNonArchived?.length ?? 0;
            },
          },

          {
            field: 'alcohol',
            filter: 'agNumberColumnFilter',
            valueFormatter: percentFormatter,
          },

          {
            field: 'lastSampleReceived',
            filter: 'agDateColumnFilter',
            roles: ['user'],

            valueFormatter: dateFormatter,
            valueGetter: (params: any) => {
              const samples: Samples[] = params.data?.samples;

              const lastSamplesReceived = sortBy(samples, 'samplingDate');
              const lastSample =
                lastSamplesReceived[lastSamplesReceived.length - 1];
              return lastSample?.samplingDate;
            },
          },

          {
            field: 'commodityType',
            roles: ['user'],

            valueGetter: (params: any) =>
              getValueForOptions(
                'commodityType',
                commodityOptions,
                params.data?.commodityType
              ),
          },

          {
            field: 'storageLocationId',
            roles: ['user'],

            dataType: 'addresses',
          },

          {
            field: 'supplierContactId',
            roles: ['user'],

            dataType: 'contacts',
          },

          {
            field: 'conditioning',
            roles: ['user'],
          },
          {
            field: 'harvestMethod',
            roles: ['user'],
          },
          {
            field: 'subGeneric',
            roles: ['user'],
          },

          {
            field: 'officeId',
            roles: ['user'],

            dataType: 'offices',
          },

          {
            field: 'additionalInformations',
            valueFormatter: plainTextFromFormatter as any,
            exportType: 'htmlToText',
          },

          {
            field: 'netsuiteId',
            roles: ['user'],
          },
          {
            field: 'comments',
            roles: ['user'],

            valueFormatter: plainTextFromFormatter as any,
            exportType: 'htmlToText',
          },
          ...this.getVarietalsColumn('Appellation', 'appellations'),
          ...this.getVarietalsColumn('Varietal', 'varietals'),
          ...this.getVarietalsColumn('Vintage', 'vintages'),
        ] as any;
      },

      companies: async () => {
        const currenciesConversions = await firstValueFrom(
          this.storeService.store.currenciesConversions.data$.pipe(take(1))
        );

        return [
          {
            field: 'name',
            cellDataType: 'text',
          },
          { field: 'category' },
          { field: 'certifications' },
          {
            field: 'scoring',
          },
          { field: 'parentCompany' },
          {
            field: 'officeId',
            dataType: 'offices',
          },
          {
            field: 'brokerId',
            dataType: 'users',
          },
          {
            field: 'country',
            valueGetter: (params: any) =>
              getValueForOptions(
                'countries',
                getCountryOptions(),
                params.data?.country
              ),
          },

          { field: 'cvi' },
          { field: 'fda' },
          { field: 'eori' },

          { field: 'email' },
          { field: 'excise' },
          { field: 'officialId' },
          { field: 'phone' },
          { field: 'vat' },
          {
            field: 'targetObjective',
            filter: 'agNumberColumnFilter',
            valueFormatter: (params: any) => {
              return currencyFormatter(
                params,
                params.data?.targetObjectiveCurrency
              );
            },
          },
          {
            headerName: 'Target Objective ($USD)',
            filter: 'agNumberColumnFilter',
            valueGetter: (params: any) => {
              return convertCurrencyToUSD(
                currenciesConversions,
                params.data?.createdAt,
                params.data?.targetObjectiveCurrency,
                params.data?.targetObjective
              );
            },
            valueFormatter: (params: any) => {
              return currencyFormatter(params, 'USD', '1.0-0');
            },
          },
          {
            headerName: 'Broker commission ($USD, this year)',
            filter: 'agNumberColumnFilter',
            valueGetter: (params: any) => {
              const agreements: Agreements[] = params.data?.agreements;

              const totalCommission = sum(
                agreements
                  .filter((a) => {
                    return (
                      isSameYear(parseISO(a.createdAt), new Date()) &&
                      a.contractStatus === 'approvedByParties'
                    );
                  })
                  .map((agreement) => {
                    const commission =
                      this.agreementsService.getCommissionFromAgreement(
                        agreement
                      );

                    const currency = agreement.currency;

                    if (!commission || !currency) {
                      return 0;
                    }

                    return convertCurrencyToUSD(
                      currenciesConversions,
                      agreement?.createdAt,
                      currency,
                      commission
                    );
                  })
              );

              return totalCommission;
            },
            valueFormatter: (params: any) => {
              return currencyFormatter(params, 'USD', '1.0-0');
            },
          },
          {
            field: 'agreementsCount',
            // filter: 'agNumberColumnFilter',
            cellDataType: 'number',
          },
          {
            field: 'comments',
            valueFormatter: plainTextFromFormatter as any,
            exportType: 'htmlToText',
          },
        ] as any;
      },

      contacts: async () => {
        return [
          {
            field: 'firstname',
          },
          { field: 'lastname' },
          {
            field: 'companyId',
            dataType: 'companies',
          },
          {
            field: 'addressId',
            dataType: 'addresses',
          },
          {
            field: 'brokerId',

            dataType: 'users',
          },
          { field: 'jobTitle' },
          { field: 'phone' },
          { field: 'mobile' },
          { field: 'officePhone' },
          { field: 'altPhone' },
          { field: 'fax' },
          { field: 'email' },
          { field: 'altEmail' },
          { field: 'categories' },
          {
            field: 'comments',
            valueFormatter: plainTextFromFormatter as any,
            exportType: 'htmlToText',
          },
        ];
      },

      addresses: async () => {
        return [
          {
            field: 'name',
          },
          {
            field: 'excise',
          },
          {
            field: 'storageCompany',
          },
          {
            field: 'bondedWarehouse',
          },
          {
            field: 'country',
            valueGetter: (params: any) =>
              getValueForOptions(
                'countries',
                getCountryOptions(),
                params.data?.country
              ),
          },
          {
            field: 'state',
          },
          { field: 'city' },
          { field: 'zipcode' },
          { field: 'streetNumber' },
          { field: 'street1' },
          { field: 'street2' },
          {
            field: 'comments',
            valueFormatter: plainTextFromFormatter as any,
            exportType: 'htmlToText',
          },
        ] as any;
      },

      buySpecs: async () => {
        const currenciesConversions = await firstValueFrom(
          this.storeService.store.currenciesConversions.data$.pipe(take(1))
        );

        return [
          {
            field: 'deadline',
            valueFormatter: dateFormatter,
            filter: 'agDateColumnFilter',
          },
          {
            field: 'commodityType',
            valueGetter: (params: any) =>
              getValueForOptions(
                'commodityType',
                commodityOptions,
                params.data?.commodityType
              ),
          },

          {
            field: 'buyerCompanyId',
            dataType: 'companies',
            roles: ['user'],
          },
          {
            field: 'buyerContactId',
            dataType: 'contacts',
            roles: ['user'],
          },
          {
            field: 'buyerAddressId',
            dataType: 'addresses',
            roles: ['user'],
          },
          {
            field: 'quantityNeeded',
            filter: 'agNumberColumnFilter',
            ...(this.userService.role$.value === 'user'
              ? this.editField<BuySpecs>('buySpecs', (val) => {
                  return { quantityNeeded: <number>val };
                })
              : {}),
            valueFormatter: numberFormatter,
          },
          {
            field: 'uom',
            valueFormatter: (params: any) => {
              return getUom(params.data?.uom);
            },
          },

          ...VarietalsColumnd,
          ...VintagesColumnd,
          ...AppellationTreeColumns,

          {
            field: 'currency',
            roles: ['user'],
            valueGetter: (params: any) =>
              getValueForOptions(
                'currency',
                currencyOptions,
                params.data?.currency
              ),
          },
          {
            field: 'askPrice',
            filter: 'agNumberColumnFilter',
            ...(this.userService.role$.value === 'user'
              ? this.editField<BuySpecs>('buySpecs', (val) => {
                  return { askPrice: <number>val };
                })
              : {}),
            valueFormatter: (params: any) => {
              return currencyFormatter(params, params.data?.currency);
            },
          },

          {
            field: 'isOpportunistic',
            roles: ['user'],
          },

          {
            field: 'certifications',
          },

          {
            field: 'alcohol',
            filter: 'agNumberColumnFilter',
            valueFormatter: percentFormatter,
          },

          {
            field: 'brokerId',
            dataType: 'users',
            roles: ['user'],
          },

          {
            field: 'generic',
            roles: ['user'],
          },
          {
            field: 'subGeneric',
            roles: ['user'],
          },

          {
            field: 'qualities',
          },
          {
            field: 'officeId',
            dataType: 'offices',
            roles: ['user'],
          },

          {
            field: 'commissionAmount',
            filter: 'agNumberColumnFilter',
            valueFormatter: (params: any) => {
              return currencyFormatter(params, params.data?.currency);
            },
            roles: ['user'],
          },
          {
            headerName: 'Commission Amount ($USD)',
            filter: 'agNumberColumnFilter',
            valueGetter: (params: any) => {
              return convertCurrencyToUSD(
                currenciesConversions,
                params.data?.createdAt,
                params.data?.currency,
                params.data?.commissionAmount
              );
            },
            valueFormatter: (params: any) => {
              return currencyFormatter(params, 'USD', '1.0-0');
            },
            roles: ['user'],
          },
          {
            field: 'commissionPercentage',
            filter: 'agNumberColumnFilter',
            valueFormatter: numberFormatter,
            roles: ['user'],
          },
          {
            field: 'agreementsIds',
            // dataType: 'agreements',
            roles: ['user'],
          },
          {
            field: 'comments',
            roles: ['user'],

            valueFormatter: plainTextFromFormatter as any,
            exportType: 'htmlToText',
          },
          ...this.getVarietalsColumn('Appellation', 'appellations'),
          ...this.getVarietalsColumn('Varietal', 'varietals'),
          ...this.getVarietalsColumn('Vintage', 'vintages'),
        ] as any;
      },
      samples: async () => {
        const currenciesConversions = await firstValueFrom(
          this.storeService.store.currenciesConversions.data$.pipe(take(1))
        );

        return [
          {
            field: 'itemId',
            dataType: 'items',
            filter: 'agNumberColumnFilter',
            cellRenderer: LinkComponent,
            cellRendererParams: {
              dataType: 'items',
            },
          },
          {
            field: 'shelf',
          },
          {
            field: 'samplingDate',
            valueFormatter: dateFormatter,
            filter: 'agDateColumnFilter',
          },
          {
            field: 'reminderForExpiration',
            valueFormatter: dateFormatter,
            filter: 'agDateColumnFilter',
          },
          {
            field: 'sampledBy',
            dataType: 'users',
          },
          {
            field: 'sampleShipmentId',
          },
          {
            field: 'officeId',
            dataType: 'offices',
          },
          {
            field: 'item.commodityType',
            valueGetter: (params: any) =>
              getValueForOptions(
                'commodityType',
                commodityOptions,
                params.data?.item?.commodityType
              ),
          },
          {
            field: 'item.archivedAt',
            valueFormatter: dateFormatter,
            valueGetter: (params: any) => params.data?.item?.archivedAt,
          },
          {
            field: 'item.supplierCompanyId',
            dataType: 'companies',
          },
          {
            field: 'varietals',
            valueGetter: (params: any) => {
              return (params.data?.item as Items)?.varietals?.map(
                (v) => v.type
              );
            },
            valueFormatter: (params: any) => {
              return getDisplaysForVariatealOrVintages(
                params.data?.item?.varietals
              );
            },
          },
          {
            field: 'vintages',
            valueGetter: (params: any) => {
              return (params.data?.item as Items)?.vintages?.map((v) => v.type);
            },
            valueFormatter: (params: any) => {
              return getDisplaysForVariatealOrVintages(
                params.data?.item?.vintages
              );
            },
          },
          {
            field: 'item.generic',
          },
          {
            field: 'item.country',
          },
          {
            field: 'item.state',
          },
          {
            field: 'item.appellations',
            valueGetter: (params: any) => {
              return (params.data?.item as Items)?.appellations?.map(
                (v) => v.type
              );
            },
            valueFormatter: (params: any) => {
              return getDisplaysForVariatealOrVintages(
                params?.data?.item?.appellations
              );
            },
          },
          {
            field: 'item.subAppellation',
          },
          {
            field: 'item.quantity',
            valueFormatter: (params: any) => {
              return numberFormatter(params, getUom(params?.data?.item?.uom));
            },
          },
          {
            field: 'item.localPrice',
            valueFormatter: (params: any) => {
              return currencyFormatter(params, params?.data?.item?.currency);
            },
          },
          {
            headerName: 'Item Local Price ($USD)',
            filter: 'agNumberColumnFilter',
            valueGetter: (params: any) => {
              return convertCurrencyToUSD(
                currenciesConversions,
                params.data?.createdAt,
                params.data?.item?.currency,
                params.data?.item?.localPrice
              );
            },
            valueFormatter: (params: any) => {
              return currencyFormatter(params, 'USD', '1.0-0');
            },
          },
          {
            field: 'item.lotNo',
          },
          {
            field: 'ref',
          },
        ] as any;
      },
      agreements: async () => {
        const currenciesConversions = await firstValueFrom(
          this.storeService.store.currenciesConversions.data$.pipe(take(1))
        );

        return [
          {
            field: 'commodityType',
            valueGetter: (params: any) =>
              getValueForOptions(
                'commodityType',
                commodityOptions,
                params.data?.commodityType
              ),
          },
          {
            field: 'contractStatus',
            valueGetter: (params: any) =>
              getValueForOptions(
                'contractStatus',
                agreementsContractStatusOptions,
                params.data?.contractStatus
              ),
          },
          {
            field: 'thirdPartyContractRef',
          },
          {
            field: 'buyerCompanyId',
            dataType: 'companies',
          },
          {
            field: 'buyerCiattiCommission',
            filter: 'agNumberColumnFilter',
            valueFormatter: percentFormatter,
          },
          {
            field: 'buyerContactId',
            dataType: 'contacts',
          },
          {
            field: 'buyerContractRef',
          },
          { field: 'buyerEori' },
          { field: 'buyerExcise' },
          { field: 'buyerFda' },
          { field: 'buyerVat' },
          {
            field: 'buyerOfficeId',
            dataType: 'offices',
          },
          {
            field: 'buyerSpecsId',
          },
          {
            field: 'buyerUserId',
            headerName: 'Buyer Broker',
            dataType: 'users',
          },
          {
            field: 'commissionAmount',
            filter: 'agNumberColumnFilter',
            valueGetter: (params: any) => {
              return this.agreementsService.getCommissionFromAgreement(
                params.data
              );
            },
            valueFormatter: (params: any) => {
              return currencyFormatter(params, params.data?.commissionCurrency);
            },
          } as any,
          {
            headerName: 'Commission Amount ($USD)',
            filter: 'agNumberColumnFilter',
            valueGetter: (params: any) => {
              const commission =
                this.agreementsService.getCommissionFromAgreement(params.data);

              if (!isNumber(commission)) {
                return;
              }

              return convertCurrencyToUSD(
                currenciesConversions,
                params.data.createdAt,
                params.data?.currency,
                commission
              );
            },
            valueFormatter: (params: any) => {
              return currencyFormatter(params, 'USD', '1.0-0');
            },
          } as any,
          {
            field: 'commissionBuyer',
            filter: 'agNumberColumnFilter',
            valueFormatter: percentFormatter,
          },
          {
            field: 'commissionCostBase',
            filter: 'agNumberColumnFilter',
            valueFormatter: (params: any) => {
              return currencyFormatter(params, params.data?.commissionCurrency);
            },
          },
          {
            field: 'commissionCurrency',
            valueGetter: (params: any) =>
              getValueForOptions(
                'currency',
                currencyOptions,
                params.data?.commissionCurrency
              ),
          },
          {
            field: 'commissionFeesPerUom',
            filter: 'agNumberColumnFilter',
            valueFormatter: (params: any) => {
              return currencyFormatter(params, params.data?.commissionCurrency);
            },
          },
          {
            field: 'commissionPercentage',
            filter: 'agNumberColumnFilter',
            valueFormatter: percentFormatter,
          },
          {
            field: 'commissionQuantityBase',
            filter: 'agNumberColumnFilter',
            valueFormatter: (params: any) => {
              return currencyFormatter(params, params.data?.commissionCurrency);
            },
          },
          {
            field: 'commissionSeller',
            filter: 'agNumberColumnFilter',
            valueFormatter: percentFormatter,
          },
          {
            field: 'commissionThirdParty',
            filter: 'agNumberColumnFilter',
            valueFormatter: percentFormatter,
          },
          {
            field: 'commissionType',
          },
          {
            field: 'commissionUom',
            valueFormatter: (params: any) => {
              return getUom(params.data?.commissionUom);
            },
          },
          {
            field: 'contractRevision',
          },
          {
            field: 'contractStatus',
            valueGetter: (params: any) =>
              getValueForOptions(
                'agContractStatus',
                agreementsContractStatusOptions,
                params.data?.contractStatus
              ),
          },
          {
            field: 'fulfillmentsStatus',
            valueGetter: (params: any) => {
              const status =
                params.data?.fulfillmentsStatus ??
                getFulfillmentsStatus(params.data);

              return getValueForOptions(
                'agFulfillmentsStatus',
                agreementsFulfilledStatusOptions,
                status
              );
            },
          },
          {
            headerName: 'Completion',
            valueGetter: (params: any) => {
              return this.agreementsService.getCompletionFromAgreement(
                params.data
              );
            },
            valueFormatter: (params: any) => {
              if (params.value === undefined) {
                return '';
              }
              return formatPercent(params.value);
            },
          },
          {
            field: 'contractUserId',
            headerName: 'Issued by',
            dataType: 'users',
          },
          {
            field: 'endDate',
            valueFormatter: dateFormatter,
            filter: 'agDateColumnFilter',
          },
          {
            field: 'files',

            valueFormatter: (params: any) => {
              return (params?.data?.files as File[])?.map((f) => f.name) as any;
            },
          },
          {
            field: 'incoterms',
            valueGetter: (params: any) =>
              getValueForOptions(
                'incoterms',
                incotermsOptions,
                params.data?.incoterms
              ),
          },
          {
            field: 'itemsId',
          },
          {
            field: 'thirdPartyCompanyId',
            dataType: 'companies',
          },
          {
            field: 'thirdPartyContactId',
            dataType: 'contacts',
          },
          {
            field: 'place',
          },
          {
            field: 'reminders',
          },
          {
            field: 'sellerCiattiCommission',
            filter: 'agNumberColumnFilter',
            valueFormatter: percentFormatter,
          },
          {
            field: 'sellerCompanyId',
            dataType: 'companies',
          },
          {
            field: 'sellerContactId',
            dataType: 'contacts',
          },
          {
            field: 'sellerContractRef',
          },
          {
            field: 'sellerBondedWarehouse',
          },
          {
            field: 'sellerEori',
          },
          {
            field: 'sellerExcise',
          },
          {
            field: 'sellerFda',
          },
          {
            field: 'sellerVat',
          },
          {
            field: 'sellerOfficeId',
            dataType: 'offices',
          },
          {
            headerName: 'Seller Broker',
            field: 'sellerUserId',
            dataType: 'users',
          },
          {
            field: 'startDate',
            valueFormatter: dateFormatter,
            filter: 'agDateColumnFilter',
          },
          {
            field: 'terms',
            valueFormatter: plainTextFromFormatter as any,
            exportType: 'htmlToText',
          },
          {
            field: 'termsAddContract',
            valueFormatter: plainTextFromFormatter as any,
            exportType: 'htmlToText',
          },
          {
            field: 'termsCommission',
            valueFormatter: plainTextFromFormatter as any,
            exportType: 'htmlToText',
          },
          {
            field: 'termsPayment',
            valueFormatter: plainTextFromFormatter as any,
            exportType: 'htmlToText',
          },
          {
            field: 'termsShipping',
            valueFormatter: plainTextFromFormatter as any,
            exportType: 'htmlToText',
          },
          {
            field: 'thirdPartyCommission',
            filter: 'agNumberColumnFilter',
            valueFormatter: percentFormatter,
          },
          {
            field: 'thirdPartyCompanyId',
            dataType: 'companies',
          },
          {
            field: 'contractOfficeId',
            dataType: 'offices',
          },
          {
            field: 'title',
          },
          {
            field: 'totalCost',
            filter: 'agNumberColumnFilter',
            valueFormatter: (params: any) => {
              return currencyFormatter(params, params.data?.commissionCurrency);
            },
          },
          {
            headerName: 'Total Cost ($USD)',
            filter: 'agNumberColumnFilter',
            valueGetter: (params: any) => {
              const totalCost = params.data?.totalCost;

              if (!isNumber(totalCost)) {
                return;
              }

              return convertCurrencyToUSD(
                currenciesConversions,
                params.data.createdAt,
                params.data?.commissionCurrency,
                totalCost
              );
            },
            valueFormatter: (params: any) => {
              return currencyFormatter(params, 'USD', '1.0-0');
            },
          },
          {
            field: 'isMultiYearContract',
          },
          {
            field: 'evergreenContract',
          },
          {
            field: 'evergreenAgreementId',
          },
          {
            headerName: 'Open balance amount',
            valueGetter: (params: any) => {
              const agreeement = params.data as Agreements;
              const commissions =
                this.agreementsService.getCommissionFromAgreement(agreeement);

              if (!commissions) {
                return;
              }

              const openBalanceAmount =
                commissions -
                sum(
                  (agreeement.invoices ?? []).map(
                    (invoice) => invoice.amount ?? 0
                  )
                );

              return openBalanceAmount;
            },
            valueFormatter: (params: any) => {
              return currencyFormatter(params, params.data?.commissionCurrency);
            },
          },
          {
            field: 'comments',
            valueFormatter: plainTextFromFormatter as any,
            exportType: 'htmlToText',
          },
          {
            field: 'additionalInformations',
            valueFormatter: plainTextFromFormatter as any,
            exportType: 'htmlToText',
          },
        ];
      },

      shipmentFulfillments: async () => {
        const currenciesConversions = await firstValueFrom(
          this.storeService.store.currenciesConversions.data$.pipe(take(1))
        );

        return [
          {
            field: 'agreementId',
          },
          {
            field: 'itemId',
            dataType: 'items',
          },
          {
            field: 'date',
            valueFormatter: dateFormatter as any,
            filter: 'agDateColumnFilter',
          },
          {
            field: 'quantity',
            filter: 'agNumberColumnFilter',
            // TODO should not use currency formatter
            valueFormatter: (params: any) => {
              return numberFormatter(params, getUom(params?.data?.uom));
            },
          },
          {
            field: 'status',
            valueGetter: (params: any) =>
              getValueForOptions(
                'shipmentFullfillmentStatus',
                statusOptionsShipmentFulfillment,
                params.data?.status
              ),
          },
          {
            field: 'invoice.createdAt',
            valueFormatter: dateFormatter as any,
          },
          {
            headerName: 'Invoiced by',
            field: 'invoice.createdBy',
            dataType: 'users',
          },
          {
            field: 'currency',
            valueGetter: (params: any) =>
              getValueForOptions(
                'currency',
                currencyOptions,
                params.data?.currency
              ),
          },
          {
            field: 'commissionAmount',
            filter: 'agNumberColumnFilter',
            valueFormatter: (params: any) => {
              return currencyFormatter(params, params.data?.currency);
            },
          },
          {
            headerName: 'Commission Amount ($USD)',
            filter: 'agNumberColumnFilter',
            valueGetter: (params: any) => {
              return convertCurrencyToUSD(
                currenciesConversions,
                params.data?.createdAt,
                params.data?.currency,
                params.data?.commissionAmount
              );
            },
            valueFormatter: (params: any) => {
              return currencyFormatter(params, 'USD', '1.0-0');
            },
          },
          {
            field: 'agreement.contractOfficeId',
            dataType: 'offices',
          },
          {
            field: 'agreement.commodityType',
            valueGetter: (params: any) =>
              getValueForOptions(
                'commodityType',
                commodityOptions,
                params.data?.agreement?.commodityType
              ),
          },
          {
            field: 'agreement.buyerContractRef',
          },
          {
            field: 'agreement.sellerContractRef',
          },
          {
            field: 'item.varietals',
            valueGetter: (params: any) => {
              return (params.data?.item as Items)?.varietals?.map(
                (v) => v.type
              );
            },
            valueFormatter: (params: any) => {
              return getDisplaysForVariatealOrVintages(
                params.data?.item?.varietals
              );
            },
          },
          {
            field: 'item.vintages',
            valueGetter: (params: any) => {
              return (params.data?.item as Items)?.vintages?.map((v) => v.type);
            },
            valueFormatter: (params: any) => {
              return getDisplaysForVariatealOrVintages(
                params.data?.item?.vintages
              );
            },
          },
          {
            field: 'item.appellations',
            valueGetter: (params: any) => {
              return (params.data?.item as Items)?.appellations?.map(
                (v) => v.type
              );
            },
            valueFormatter: (params: any) => {
              return getDisplaysForVariatealOrVintages(
                params?.data?.item?.appellations
              );
            },
          },
          {
            field: 'comments',
            valueFormatter: plainTextFromFormatter as any,
            exportType: 'htmlToText',
          },
        ] as any;
      },

      varietals: async () => {
        return [
          {
            field: 'generic',
          },
          {
            field: 'subGeneric',
          },
          {
            field: 'varietal',
          },
        ];
      },

      appellations: async () => {
        return [
          {
            field: 'subAppellation',
          },
          {
            field: 'appellation',
          },
          {
            field: 'county',
          },
          {
            field: 'district',
          },
          {
            field: 'subRegion',
          },
          {
            field: 'region',
          },
          {
            field: 'state',
          },
          {
            field: 'country',
          },
        ];
      },
    };
  }

  public async getColumnDefinitions(dataType: KeycolumnsDefs) {
    const user = this.userService.userBehaviourSubject$.value;

    console.log('getColumnDefinitions', dataType);
    const columns = await this.columnDefs[dataType]();
    if (!user) {
      return columns;
    }

    return columns.filter((column) => {
      if (!column.roles?.length) {
        return true;
      }

      return column.roles.some((role) => hasUserRole(user, role));
    });
  }

  public postProcessColumnDefs(
    columnDefs: ColDefExtended<any>[]
  ): ColDefExtended<any>[] {
    const columnDefDataTypes: StoreDataTypeKey[] = uniq(
      columnDefs
        .map((columnDef) => {
          return columnDef.dataType;
        })
        .filter((r) => !!r) as any as StoreDataTypeKey[]
    );

    // should be type T and can be refactored with sotre by keys
    const datasByKeys: Partial<
      Record<
        DataTypeKeys,
        {
          [key: string]: any | undefined;
        }
      >
    > = {};
    columnDefDataTypes.forEach(async (dataType) => {
      datasByKeys[dataType] = await firstValueFrom(
        this.storeService.getStoreByKeys$<Contacts>(dataType).pipe(take(1))
      );
    });

    console.log('x', columnDefs);
    const x = columnDefs.map((columnDef) => {
      if (!columnDef.dataType) {
        return columnDef;
      }

      return {
        ...columnDef,
        valueGetter: (params: any) => {
          const fieldPath = columnDef.field;
          if (!fieldPath) {
            return;
          }

          const dataId: number | number[] = get(params?.data, fieldPath);
          if (!dataId) {
            return;
          }

          // not necessary but ts complains
          if (!columnDef.dataType) {
            return;
          }

          const labelFunction = this.formatter[columnDef.dataType];
          if (!labelFunction) {
            console.log('not label function for', columnDef.dataType);
            return dataId;
          }

          if (isArray(dataId)) {
            return dataId
              .map((id) => {
                const realItem = (datasByKeys as any)[
                  (columnDef as any).dataType
                ]?.[id];
                if (!realItem) {
                  return;
                }
                return labelFunction(realItem);
              })
              .filter((r) => !!r)
              .join(', ');
          } else {
            const realItem = datasByKeys[columnDef.dataType]?.[dataId];
            if (!realItem) {
              return;
            }
            return labelFunction(realItem);
          }
        },
      } as any;
    });

    console.log('xx', x);

    return x;
  }

  public transformFirstColumnToActionColumn(col: ColDef): ColDef {
    return {
      ...col,
      width: 166,
      headerCheckboxSelection: true,
      checkboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true,
    };
  }

  public editField = <T>(
    dataType: StoreDataTypeKey,
    getObjectToSave: (value: string | number | null) => Partial<T>,
    getId: (params: any) => number = (params) => params.data.id
  ) => {
    return {
      editable: true,
      valueSetter: (params: any) => {
        console.log('params.newValue', params.newValue);
        const parsedNewValue: number | null = params.newValue;

        if (params.oldValue === parsedNewValue) {
          return false;
        }

        const objectToSave = getObjectToSave(parsedNewValue);
        params.data = {
          ...params.data,
          ...objectToSave,
        };

        this.storeService
          .updateObjectByPk(dataType, getId(params), objectToSave)
          .subscribe(() => {
            this.messages.success('Updated');
          });

        return true;
      },
    };
  };

  private getVarietalsColumn(
    text: string,
    variableKey: 'varietals' | 'vintages' | 'appellations'
  ): ColDef[] {
    const numberOfColumns = 3;

    return new Array(numberOfColumns).fill(0).map((_, index) => {
      return {
        headerName: `${text} ${index + 1}`,

        valueGetter: (params: any) => {
          return params.data?.[variableKey]?.[index]?.type;
        },
        roles: ['user'],
      };
    });
  }
}
